(() => {
  const SELECTOR = '[data-realtor-landing]';

  const numberFrom = (value) => Number.parseInt(value, 10) || 0;

  function initialize(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const offerButtons = [...root.querySelectorAll('[data-offer]')];
    const colorRows = [...root.querySelectorAll('[data-color-row]')];
    const addButton = root.querySelector('[data-add-to-cart]');
    const cartError = root.querySelector('[data-cart-error]');
    const summary = root.querySelector('[data-summary]');
    const allocation = root.querySelector('[data-allocation]');
    const allocationMessage = root.querySelector('[data-allocation-message]');
    const totalOutput = root.querySelector('[data-total]');
    const totalNeededOutput = root.querySelector('[data-total-needed]');
    const packSizeOutput = root.querySelector('[data-pack-size]');
    const selectedQuantityOutput = root.querySelector('[data-selected-quantity]');
    const evenSplitButton = root.querySelector('[data-even-split]');

    let selectedOffer = offerButtons.find((button) => button.classList.contains('is-selected')) || offerButtons[0];

    const getPackSize = () => numberFrom(selectedOffer?.dataset.quantity);

    const counts = () => Object.fromEntries(
      colorRows.map((row) => [row.dataset.colorRow, numberFrom(row.querySelector('[data-count]')?.textContent)])
    );

    const totalCount = () => Object.values(counts()).reduce((total, count) => total + count, 0);

    const setCount = (row, count) => {
      const output = row.querySelector('[data-count]');
      if (output) output.textContent = String(Math.max(0, count));
    };

    const colorSummary = () => {
      const current = counts();
      return [
        `${current.ocean || 0} Ocean Blue`,
        `${current.stone || 0} Desert Stone`,
        `${current.green || 0} Palmetto Green`
      ].join(' · ');
    };

    const update = () => {
      const packSize = getPackSize();
      const total = totalCount();
      const valid = total === packSize;

      if (packSizeOutput) packSizeOutput.textContent = String(packSize);
      if (totalOutput) totalOutput.textContent = String(total);
      if (totalNeededOutput) totalNeededOutput.textContent = String(packSize);
      if (selectedQuantityOutput) selectedQuantityOutput.textContent = `Selected: ${packSize}`;
      if (summary) summary.textContent = colorSummary();

      if (allocation) {
        allocation.classList.toggle('is-valid', valid);
        allocation.classList.toggle('is-invalid', !valid);
      }

      if (allocationMessage) {
        allocationMessage.textContent = valid
          ? '✓ Color total complete'
          : total < packSize
            ? `Choose ${packSize - total} more`
            : `Remove ${total - packSize}`;
      }

      if (addButton) {
        addButton.disabled = !valid;
        addButton.setAttribute('aria-disabled', String(!valid));
        addButton.textContent = `ADD ${packSize} TO BAG | ${selectedOffer?.dataset.price || ''}`;
      }

      colorRows.forEach((row) => {
        const count = numberFrom(row.querySelector('[data-count]')?.textContent);
        const decrease = row.querySelector('[data-decrease]');
        const increase = row.querySelector('[data-increase]');
        if (decrease) decrease.disabled = count === 0;
        if (increase) increase.disabled = total >= packSize;
      });
    };

    const splitEvenly = () => {
      const packSize = getPackSize();
      const base = Math.floor(packSize / colorRows.length);
      let remainder = packSize % colorRows.length;

      colorRows.forEach((row) => {
        setCount(row, base + (remainder > 0 ? 1 : 0));
        remainder -= remainder > 0 ? 1 : 0;
      });
      update();
    };

    offerButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectedOffer = button;
        offerButtons.forEach((offer) => {
          const selected = offer === button;
          offer.classList.toggle('is-selected', selected);
          offer.setAttribute('aria-pressed', String(selected));
        });
        splitEvenly();
        if (cartError) cartError.hidden = true;
      });
    });

    colorRows.forEach((row) => {
      row.querySelector('[data-decrease]')?.addEventListener('click', () => {
        setCount(row, numberFrom(row.querySelector('[data-count]')?.textContent) - 1);
        update();
      });

      row.querySelector('[data-increase]')?.addEventListener('click', () => {
        if (totalCount() < getPackSize()) {
          setCount(row, numberFrom(row.querySelector('[data-count]')?.textContent) + 1);
          update();
        }
      });
    });

    evenSplitButton?.addEventListener('click', splitEvenly);

    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const main = root.querySelector('[data-gallery-main]');
        if (!main) return;
        main.src = thumb.dataset.image;
        main.alt = thumb.dataset.alt || '';
        root.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    root.querySelectorAll('[data-play-video]').forEach((button) => {
      const video = button.closest('.realtor-video')?.querySelector('[data-video]');
      if (!video) return;

      button.addEventListener('click', async () => {
        root.querySelectorAll('[data-video]').forEach((otherVideo) => {
          if (otherVideo !== video) otherVideo.pause();
        });

        if (video.paused) {
          try {
            await video.play();
          } catch (error) {
            return;
          }
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', () => {
        button.textContent = 'Ⅱ';
        button.setAttribute('aria-label', button.getAttribute('aria-label')?.replace(/^Play/, 'Pause') || 'Pause video');
      });

      video.addEventListener('pause', () => {
        button.textContent = '▶';
        button.setAttribute('aria-label', button.getAttribute('aria-label')?.replace(/^Pause/, 'Play') || 'Play video');
      });
    });

    addButton?.addEventListener('click', async () => {
      if (totalCount() !== getPackSize()) return;

      const current = counts();
      const variants = {
        ocean: root.dataset.oceanVariant,
        stone: root.dataset.stoneVariant,
        green: root.dataset.greenVariant
      };
      const allocationText = colorSummary();
      const offerName = selectedOffer.dataset.name || `${getPackSize()}-blanket pack`;
      const items = Object.entries(current)
        .filter(([color, quantity]) => quantity > 0 && variants[color])
        .map(([color, quantity]) => ({
          id: Number(variants[color]),
          quantity,
          properties: {
            'Realtor offer': offerName,
            'Color allocation': allocationText
          }
        }));

      if (!items.length) return;

      addButton.disabled = true;
      addButton.textContent = 'ADDING TO BAG…';
      if (cartError) cartError.hidden = true;

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const addUrl = window.routes?.cart_add_url || `${rootPath}cart/add.js`;
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || result.message || 'The blankets could not be added to your bag.');
        }

        const discountCode = selectedOffer.dataset.code;
        const cartPath = `${rootPath}cart`;
        window.location.assign(discountCode
          ? `${rootPath}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(cartPath)}`
          : cartPath);
      } catch (error) {
        if (cartError) {
          cartError.textContent = error.message || 'Something went wrong. Please try again.';
          cartError.hidden = false;
        }
        update();
      }
    });

    update();
  }

  const initializeAll = (scope = document) => {
    if (scope.matches?.(SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
